import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SignupSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const isPaypal = searchParams.get('paypal') === '1';
  const slug = searchParams.get('slug');

  const [status, setStatus] = useState('verifying');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId && !isPaypal) { setStatus('error'); return; }

    const verify = async () => {
      try {
        let res;
        if (isPaypal && slug) {
          res = await fetch(`${API}/api/signup/verify-paypal?slug=${slug}`);
        } else {
          res = await fetch(`${API}/api/signup/verify?session_id=${sessionId}`);
        }
        const d = await res.json();
        if (d.success) {
          setData(d);
          setStatus('success');
        } else {
          setStatus(d.payment_status === 'unpaid' || d.payment_status === 'APPROVAL_PENDING' ? 'pending' : 'error');
        }
      } catch {
        setStatus('error');
      }
    };
    verify();
  }, [sessionId, isPaypal, slug]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="max-w-md w-full text-center">
        {status === 'verifying' && (
          <div data-testid="signup-verifying">
            <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4" style={{ color: '#002FA7' }} />
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Verifying your payment...</h1>
            <p className="text-sm text-slate-500 mt-2">This will only take a moment.</p>
          </div>
        )}

        {status === 'success' && data && (
          <div data-testid="signup-success">
            <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: '#EBF5FF' }}>
              <CheckCircle2 className="h-8 w-8" style={{ color: '#002FA7' }} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Welcome aboard!</h1>
            <p className="text-slate-500 mt-2 text-sm">
              Your <strong>{data.business_name}</strong> account is ready. We've sent your login credentials to your email.
            </p>
            <p className="text-slate-500 mt-2 text-sm">
              Let's set up your business profile so we can start working on your online presence.
            </p>
            <Button
              onClick={() => navigate(data.onboarding_url)}
              className="mt-6 h-11 px-8 text-sm"
              style={{ background: '#002FA7' }}
              data-testid="go-to-onboarding-btn"
            >
              Complete Your Business Profile <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <p className="text-[10px] text-slate-400 mt-4">This takes about 5 minutes and helps us optimize your listings, SEO, and marketing.</p>
          </div>
        )}

        {status === 'pending' && (
          <div data-testid="signup-pending">
            <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4 text-amber-500" />
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Payment is processing...</h1>
            <p className="text-sm text-slate-500 mt-2">Your payment hasn't been confirmed yet. Please wait a moment and refresh.</p>
            <Button variant="outline" onClick={() => window.location.reload()} className="mt-4">Refresh</Button>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="signup-error">
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Something went wrong</h1>
            <p className="text-sm text-slate-500 mt-2">We couldn't verify your payment. Please contact support.</p>
            <Button variant="outline" onClick={() => navigate('/')} className="mt-4">Back to Home</Button>
          </div>
        )}
      </div>
    </div>
  );
}
